import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { isReceiptLedgerType, isIssueLedgerType } from '@/lib/ledgerTypes';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from 'sonner';
import { formatINR, formatCarats, formatDateTime, formatRequirementDropdownLabel } from '@/lib/format';
import { ArrowLeft, Plus, ArrowDownToLine, ArrowUpFromLine, AlertTriangle, CheckCircle2, Trash2, ShieldAlert, Printer, FileSpreadsheet, ArrowRightLeft, ShoppingCart, ChevronDown, MoreHorizontal, ShieldCheck, PackageX, Scissors } from 'lucide-react';
import { extractError } from '@/lib/errors';
// Wave 3.49 — portal-wide carat/piece validation (Rule 1 + Rule 2).
import { validateMovementQty, validateCaratMovement } from '@/lib/caratValidation';
import MemoLink from '@/components/phase7/MemoLink';
import ReturnMemoLink from '@/components/phase7/ReturnMemoLink';
import { PacketLedgerViewToggle, MergedLedgerRows, MergedLedgerDialog } from '@/components/ledger/PacketLedgerMerged';
import { buildMergedLedgerGroups } from '@/lib/ledgerMerge';

const LEDGER_VIEW_MODE_KEY = 'packet.ledger.viewMode';
import ReturnDialog from '@/components/phase7/ReturnDialog';
import { VendorFormDialog } from '@/components/phase10/VendorFormDialog';
import { ConvertToStockDialog } from '@/components/phase11/ConvertToStockDialog';
import PacketTransferDialog from '@/components/phase17/PacketTransferDialog';
import { RecutFromBrokenDialog, SendToBrokenDialog } from '@/components/broken-packets/RecutAndBreakDialogs';
import PacketNotFound from '@/components/phase17/PacketNotFound';
import PurchaseReturnDialog from '@/components/phase18/PurchaseReturnDialog';
import SaleDialog from '@/components/phase18/SaleDialog';
// Wave 3.56 — Dedicated Approval Return dialog for on-approval packets.
import ApprovalReturnDialog from '@/components/raw-material/ApprovalReturnDialog';
// Wave 3.59 — Issue-for-recutting dialog for broken packets.
import IssueForRecutDialog from '@/components/broken-packets/IssueForRecutDialog';
import { PacketImageCard } from '@/components/PacketImageCard';
import { BrokenBadge, BrokenBanner } from '@/components/broken-packets/BrokenBadge';
// Wave 3.51 Phase B — Edit Request lock banner + per-ledger-row kebab.
import { PacketEditLockBanner } from '@/components/edit-request/PacketEditLockBanner';
import { PacketLedgerEditKebab } from '@/components/edit-request/PacketLedgerEditKebab';
import { EDITED_ROW_CLASS } from '@/lib/editFlow';
import { Pencil } from 'lucide-react';

export default function PearlPacketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="Packet Detail"
      subtitle="Receive, issue, and view the full ledger of this pearl packet."
      right={
        <Button variant="outline" onClick={() => navigate(-1)} className="h-9" data-testid="packet-back-button">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      }
    >
      <CompanyGate>
        <PacketDetailContent id={id} companyId={effectiveCompanyId} />
      </CompanyGate>
      <ScrollToTopButton />
    </AppShell>
  );
}

function PacketDetailContent({ id, companyId }) {
  // Wave 3.41 — Restricted users (no values.view) hide Rate / Value
  // surfaces: header pills, the Value stat, summary INR figures, and
  // ledger Value columns. Quantity columns remain visible.
  const { user: currentUser, isSuperAdmin, isCompanyAdmin, canViewValues } = useAuth();
  const navigate = useNavigate();
  const [packet, setPacket] = useState(null);
  const [entries, setEntries] = useState([]);
  // Wave 4.10k — Detailed vs Merged ledger view (persisted). Merged collapses
  // same-day, same-party movements into one clickable row (mirrors Gold Ledger).
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem(LEDGER_VIEW_MODE_KEY) === 'merged' ? 'merged' : 'detailed'; }
    catch { return 'detailed'; }
  });
  useEffect(() => { try { localStorage.setItem(LEDGER_VIEW_MODE_KEY, viewMode); } catch { /* noop */ } }, [viewMode]);
  const [mergedDetail, setMergedDetail] = useState(null);
  const mergedGroups = React.useMemo(() => buildMergedLedgerGroups(entries), [entries]);
  // Wave 3.34 — Ledger period filter + summary block (opening / receipts /
  // issues / closing). Default = all-time (blank From/To). The same logic
  // mirrors the Gold Ledger Period card.
  const [period, setPeriod] = useState({ from: '', to: '' });
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState(null); // Wave 3.10 — 404/resolve state
  const [vendors, setVendors] = useState([]);
  const [goldsmiths, setGoldsmiths] = useState([]);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  // Wave 3 — Packet Transfer
  const [transferOpen, setTransferOpen] = useState(false);
  // Wave 3.47 — Re-cut (broken→normal) and Send-to-Broken (normal→broken) dialogs.
  const [recutOpen, setRecutOpen] = useState(false);
  const [sendToBrokenOpen, setSendToBrokenOpen] = useState(false);
  // Phase 18 — Purchase Return + Sale of Raw Material
  const [purchaseReturnOpen, setPurchaseReturnOpen] = useState(false);
  const [saleOpen, setSaleOpen] = useState(false);
  // Wave 3.56 — Approval Return (return on-approval material to vendor
  // without converting to stock first).
  const [approvalReturnOpen, setApprovalReturnOpen] = useState(false);
  // Wave 3.59 — Issue for Recutting (broken packet → vendor / cutter).
  const [issueRecutOpen, setIssueRecutOpen] = useState(false);
  const [returnFromGsOpen, setReturnFromGsOpen] = useState(false);
  const [returnGoldsmithId, setReturnGoldsmithId] = useState('');
  // Wave 3.34 — "Return from goldsmith" picker (when 2+ candidate goldsmiths
  // are holding stock from this packet). Single candidate routes directly to
  // the existing ReturnDialog without an interstitial pick step.
  const [returnPickOpen, setReturnPickOpen] = useState(false);
  // Phase 10 — Fixed rate edit dialog
  const [rateDlgOpen, setRateDlgOpen] = useState(false);
  const [rateInput, setRateInput] = useState('');
  // Phase 13.4 — Request Delete workflow
  const [deleteDlgOpen, setDeleteDlgOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [submittingDelete, setSubmittingDelete] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  // Phase 15.8 — Print Ledger PDF
  // Wave 3.34 — Print dialog removed; export now uses the active Period
  // values (or entire history when blank).
  const [printingPdf, setPrintingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const canConvert = isSuperAdmin || isCompanyAdmin;
  const canEditRate = isSuperAdmin || isCompanyAdmin;
  const canRequestDelete = isSuperAdmin || isCompanyAdmin; // Phase 13.4: SA/CA can initiate; regular users could too but we restrict UI
  // Wave 3.51 Phase B — bump to force a re-fetch of any pending Edit
  // Requests scoped to this packet (e.g. after submitting one from the
  // per-row kebab menu).
  const [editLockRefreshKey, setEditLockRefreshKey] = useState(0);
  const refreshEditLock = () => setEditLockRefreshKey((k) => k + 1);

  const load = async () => {
    try {
      // Wave 3.34 — Send `start`/`end` ISO timestamps for the active period.
      // Blank period → backend treats it as "all time" (opening=0, closing=current).
      const params = {};
      if (period.from) params.start = new Date(`${period.from}T00:00:00`).toISOString();
      if (period.to) params.end = new Date(`${period.to}T23:59:59.999`).toISOString();
      const { data } = await api.get(`/pearls/packets/${id}/ledger`, { params });
      setPacket(data.packet);
      setEntries(data.entries);
      setSummary(data.summary || null);
      setLoadError(null);
    } catch (e) {
      // Wave 3.10 — Defensive: if the packet is missing (404), try to resolve
      // its successor (converted/deleted/etc.) and show a friendly UI instead
      // of a long error page.
      const status = e?.response?.status;
      if (status === 404) {
        try {
          const { data: rez } = await api.get(`/pearls/packets/${id}/resolve`);
          setLoadError({ kind: '404', resolution: rez });
        } catch {
          setLoadError({ kind: '404', resolution: { exists: false, reason: 'missing' } });
        }
      } else {
        setLoadError({ kind: 'error', message: extractError(e, 'Failed to load packet') });
      }
    }
  };
  const loadEligibility = async () => {
    try {
      const { data } = await api.get(`/pearls/packets/${id}/delete-eligibility`);
      setEligibility(data);
    } catch (e) {
      // Non-blocking — keep eligibility null
      setEligibility(null);
    }
  };
  const loadParties = async () => {
    // Vendors / goldsmiths masters are gated by their OWN permissions
    // (vendors.view / goldsmiths.view). A material-scoped operator may lack
    // them, so fetch each independently and swallow errors — the packet
    // ledger (loaded in a separate effect) is never blocked, and the
    // Receive / Issue pickers just show no options.
    api.get('/masters/vendors', { params: { company_id: companyId } })
      .then((r) => setVendors(Array.isArray(r.data) ? r.data : []))
      .catch(() => setVendors([]));
    api.get('/masters/goldsmiths', { params: { company_id: companyId } })
      .then((r) => setGoldsmiths(Array.isArray(r.data) ? r.data : []))
      .catch(() => setGoldsmiths([]));
  };
  // Wave 3.62 — Vendors this packet has received purchase receipts from.
  const [purchaseReturnVendors, setPurchaseReturnVendors] = useState([]);
  const loadPurchaseReturnVendors = async () => {
    try {
      const { data } = await api.get(`/pearls/packets/${id}/purchase-return-vendors`);
      setPurchaseReturnVendors(Array.isArray(data) ? data : []);
    } catch {
      setPurchaseReturnVendors([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, period.from, period.to]);
  useEffect(() => { loadParties(); loadEligibility(); loadPurchaseReturnVendors(); /* eslint-disable-next-line */ }, [id]);

  const saveFixedRate = async () => {
    const r = Number(rateInput);
    if (!r || r <= 0) { toast.error('Enter a valid rate'); return; }
    try {
      await api.put(`/pearls/packets/${id}`, { fixed_rate_per_ct: r });
      toast.success(`Fixed rate updated to ${r.toLocaleString('en-IN')}/ct`);
      setRateDlgOpen(false); load();
    } catch (e) { toast.error(extractError(e, 'Rate update failed')); }
  };

  const submitDeleteRequest = async () => {
    if (!deleteReason.trim()) {
      toast.error('Please provide a reason for the deletion request');
      return;
    }
    setSubmittingDelete(true);
    try {
      const { data } = await api.post(`/pearls/packets/${id}/request-delete`, {
        reason: deleteReason.trim(),
      });
      const next = data.status === 'pending_sa'
        ? 'Stage 1 auto-approved; awaiting Super Admin approval.'
        : 'Awaiting Company Admin approval (Stage 1).';
      toast.success(`Delete request submitted. ${next}`);
      setDeleteDlgOpen(false);
      setDeleteReason('');
      await Promise.all([load(), loadEligibility()]);
    } catch (e) {
      toast.error(extractError(e, 'Failed to submit delete request'));
    } finally {
      setSubmittingDelete(false);
    }
  };

  // Wave 3.34 — Print Ledger PDF download. Always honors the currently
  // active Period filter (or downloads the entire history when blank).
  const downloadLedgerPdf = async () => {
    setPrintingPdf(true);
    try {
      const params = {};
      if (period.from) params.from_date = period.from;
      if (period.to) params.to_date = period.to;
      const res = await api.get(`/pearls/packets/${id}/ledger/pdf`, {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = (packet?.packet_no || 'packet').replace(/\//g, '-');
      a.download = `packet_ledger_${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(
        period.from || period.to
          ? 'Ledger PDF downloaded for the selected period'
          : 'Ledger PDF downloaded (entire history)'
      );
    } catch (e) {
      toast.error(extractError(e, 'Failed to download ledger PDF'));
    } finally {
      setPrintingPdf(false);
    }
  };

  // Wave 4.x — Export the ledger as Excel, mirroring the on-screen table.
  // Honors the active Period filter (or entire history when blank).
  const downloadLedgerExcel = async () => {
    setExportingExcel(true);
    try {
      const params = {};
      if (period.from) params.from_date = period.from;
      if (period.to) params.to_date = period.to;
      const res = await api.get(`/pearls/packets/${id}/ledger/excel`, {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = (packet?.packet_no || 'packet').replace(/\//g, '-');
      a.download = `packet_ledger_${slug}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(
        period.from || period.to
          ? 'Ledger Excel downloaded for the selected period'
          : 'Ledger Excel downloaded (entire history)'
      );
    } catch (e) {
      toast.error(extractError(e, 'Failed to download ledger Excel'));
    } finally {
      setExportingExcel(false);
    }
  };

  // Wave 3.34 — Compute which goldsmith(s) currently hold balance from THIS
  // packet (by summing issues - returns from the packet's ledger entries).
  // Used by the "Return from Goldsmith" Actions menu item:
  //   • 0 candidates → menu item is disabled
  //   • 1 candidate  → menu item routes directly to ReturnDialog
  //   • 2+ candidates → opens a picker dialog
  // NOTE: must be declared BEFORE early returns (rules-of-hooks).
  const returnCandidates = React.useMemo(() => {
    const map = new Map();
    for (const e of entries || []) {
      const gid = e.goldsmith_id;
      if (!gid) continue;
      const cur = map.get(gid) || {
        id: gid,
        name: e.goldsmith_name || (goldsmiths.find((g) => g.id === gid) || {}).name || gid,
        carats: 0,
        pieces: 0,
      };
      if (e.type === 'issue') {
        cur.carats += Number(e.carats || 0);
        cur.pieces += Number(e.pieces || 0);
      } else if (e.type === 'receipt' && (e.sub_type === 'goldsmith_return' || e.return_id)) {
        cur.carats -= Number(e.carats || 0);
        cur.pieces -= Number(e.pieces || 0);
      }
      map.set(gid, cur);
    }
    return Array.from(map.values()).filter((x) => x.carats > 1e-9 || x.pieces > 0);
  }, [entries, goldsmiths]);

  if (loadError?.kind === '404') {
    return <PacketNotFound material="pearl" packetId={id} resolution={loadError.resolution} />;
  }
  if (loadError?.kind === 'error') {
    return (
      <div className="py-8 text-center">
        <div className="text-rose-700 font-medium">{loadError.message}</div>
        <Button variant="outline" className="mt-3" onClick={() => navigate(-1)} data-testid="packet-error-back">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      </div>
    );
  }
  if (!packet) return <div className="text-slate-500 py-8">Loading…</div>;

  const isOnApproval = packet.purchase_status === 'on_approval';
  const isDeleted = !!packet.is_deleted;
  const openDeleteReq = eligibility?.open_request;
  const eligibleToRequestDelete = !!(eligibility?.eligible);
  const deleteBlockedReasons = eligibility?.reasons || [];

  return (
    <div>
      {isDeleted && (
        <div
          className="mb-5 flex items-start gap-3 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-rose-900"
          data-testid="packet-deleted-banner"
        >
          <ShieldAlert size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium">This packet has been deleted</div>
            <div className="text-xs text-rose-800/90 mt-0.5">
              Detail preserved for historical reference. Transactional actions are disabled.
              {packet.deleted_at ? ` · Deleted ${formatDateTime(packet.deleted_at)}` : ''}
            </div>
          </div>
        </div>
      )}
      {!isDeleted && openDeleteReq && (
        <div
          className="mb-5 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900"
          data-testid="packet-delete-pending-banner"
        >
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium">Delete request in progress</div>
            <div className="text-xs text-amber-800/90 mt-0.5">
              Status: <span className="font-mono uppercase">{openDeleteReq.status}</span>
              {openDeleteReq.requested_by_email ? ` · Requested by ${openDeleteReq.requested_by_email}` : ''}
              {openDeleteReq.requested_at ? ` · ${formatDateTime(openDeleteReq.requested_at)}` : ''}
              {' · '}
              <Link to="/requests" className="underline">Open Requests</Link>
            </div>
          </div>
        </div>
      )}
      <Card className="p-5 mb-5">
        {packet?.packet_type === 'broken' && <BrokenBanner packetNo={packet.packet_no} />}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="font-mono text-2xl bg-[var(--diamond-bg)] text-[var(--diamond-icon)] px-3 py-1 rounded-lg" data-testid="packet-detail-no">
                {packet.packet_no}
              </div>
              {packet?.packet_type === 'broken' && <BrokenBadge size="lg" />}
              <Badge variant="secondary">{packet.valuation_mode === 'fixed' ? 'Fixed Rate' : 'Running Average'}</Badge>
              {/* Wave 3.41 — Rate pills (fixed + running avg) hidden when !canViewValues */}
              {canViewValues && packet.valuation_mode === 'fixed' && (
                <button
                  type="button"
                  onClick={() => canEditRate && (setRateInput(String(packet.fixed_rate_per_ct || packet.current_rate_per_ct || 0)), setRateDlgOpen(true))}
                  disabled={!canEditRate}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-[var(--diamond-border)] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] text-xs font-mono ${canEditRate ? 'hover:bg-[var(--diamond-bg)]/80 cursor-pointer' : 'opacity-90 cursor-not-allowed'}`}
                  data-testid="packet-fixed-rate-pill"
                  title={canEditRate ? 'Edit fixed rate' : 'Fixed rate (only Super Admin / Company Admin can edit)'}
                >
                  <span className="opacity-70">Fixed Rate:</span>
                  <span className="font-medium">₹{Number(packet.fixed_rate_per_ct || packet.current_rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}/ct</span>
                  {canEditRate && <Pencil size={11} className="opacity-70" />}
                </button>
              )}
              {canViewValues && packet.valuation_mode === 'running_avg' && (
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-[var(--diamond-border)] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] text-xs font-mono"
                  data-testid="packet-running-avg-pill"
                  title="Running average rate is auto-calculated from receipts"
                >
                  <span className="opacity-70">Current Avg:</span>
                  <span className="font-medium">₹{Number(packet.current_rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}/ct</span>
                </span>
              )}
              {/* Phase 11 — Average size pill (carats per piece). Useful for sortability and packing checks. */}
              {Number(packet.balance_pieces || 0) > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 text-xs font-mono"
                  data-testid="packet-avg-size-pill"
                  title="Average size = Balance Carats ÷ Balance Pieces"
                >
                  <span className="opacity-70">Avg Size:</span>
                  <span className="font-medium">
                    {(Number(packet.balance_carats || 0) / Number(packet.balance_pieces || 1)).toFixed(3)} ct/pc
                  </span>
                </span>
              )}
              {isOnApproval ? (
                <Badge className="bg-amber-50 text-amber-800 border border-amber-200" data-testid="packet-status-badge">On Approval</Badge>
              ) : (
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="packet-status-badge">Stock (Purchased)</Badge>
              )}
            </div>
            <div className="mt-2 text-sm text-slate-600">
              <span className="font-medium">{packet.classification?.shape_name || packet.classification_shape}</span>
              {packet.description && <span> · {packet.description}</span>}
            </div>
            {isOnApproval && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded">
                Value of this packet is held in the vendor's <strong>On Approval</strong> balance and will only post to payables once converted to Stock.
              </div>
            )}
          </div>
          <div className={`grid ${canViewValues ? 'grid-cols-3' : 'grid-cols-2'} gap-3 min-w-[360px]`}>
            <Stat label="Carats" value={formatCarats(packet.balance_carats)} />
            <Stat label="Pieces" value={packet.balance_pieces} />
            {canViewValues && <Stat label="Value" value={formatINR(packet.balance_value)} />}
          </div>
        </div>
        {/* Wave 3.34 — Header action buttons consolidated into a single
            Actions dropdown for a cleaner header. All previous actions are
            preserved with their icons + enable/disable rules. */}
        <div className="mt-4 flex gap-2 flex-wrap items-center">
          {!isDeleted && (
            <PacketActionsMenu
              packet={packet}
              isOnApproval={isOnApproval}
              canConvert={canConvert}
              canRequestDelete={canRequestDelete}
              openDeleteReq={openDeleteReq}
              eligibleToRequestDelete={eligibleToRequestDelete}
              deleteBlockedReasons={deleteBlockedReasons}
              returnCandidates={returnCandidates}
              onReceive={() => setReceiveOpen(true)}
              onIssue={() => setIssueOpen(true)}
              onReturnFromGoldsmith={(gid) => { setReturnGoldsmithId(gid); setReturnFromGsOpen(true); }}
              onReturnPick={() => setReturnPickOpen(true)}
              onConvertToStock={() => setConvertOpen(true)}
              onTransfer={() => setTransferOpen(true)}
              onPurchaseReturn={() => setPurchaseReturnOpen(true)}
              purchaseReturnVendors={purchaseReturnVendors}
              onSale={() => setSaleOpen(true)}
              onApprovalReturn={() => setApprovalReturnOpen(true)}
              onSendToBroken={() => setSendToBrokenOpen(true)}
              onRecut={() => setRecutOpen(true)}
              onIssueForRecut={() => setIssueRecutOpen(true)}
              onPrint={downloadLedgerPdf}
              onExportExcel={downloadLedgerExcel}
              onRequestDelete={() => setDeleteDlgOpen(true)}
              printing={printingPdf}
              exportingExcel={exportingExcel}
              periodActive={!!(period.from || period.to)}
            />
          )}
          {isDeleted && (
            <div className="text-xs text-rose-700 italic" data-testid="packet-deleted-actions-msg">
              All transactional actions are disabled for deleted packets.
            </div>
          )}
        </div>
      </Card>

      {/* Wave 3.51 Phase B — Top lock banner: any pending Edit Request
          scoped to this packet blocks downstream activity. */}
      <PacketEditLockBanner
        packetId={id}
        refreshKey={editLockRefreshKey}
      />

      {/* Wave 3.37 — Packet image upload + preview. Edit gated to SA / CA. */}
      <PacketImageCard
        image={packet?.image}
        canEdit={!isDeleted && (isSuperAdmin || isCompanyAdmin)}
        onSave={async (newImage) => {
          await api.post(`/pearls/packets/${id}/image`, { image_data_url: newImage });
          toast.success(newImage ? 'Packet image updated' : 'Packet image cleared');
          await load();
        }}
      />

      {/* Wave 3.34 — Return-from-Goldsmith picker dialog (when multiple
          goldsmiths currently hold balance from this packet). */}
      <ReturnPickerDialog
        open={returnPickOpen}
        onOpenChange={setReturnPickOpen}
        packet={packet}
        candidates={returnCandidates}
        onPick={(gid) => { setReturnPickOpen(false); setReturnGoldsmithId(gid); setReturnFromGsOpen(true); }}
      />

      {returnFromGsOpen && returnGoldsmithId && (
        <ReturnDialog
          open={returnFromGsOpen}
          onOpenChange={(v) => { setReturnFromGsOpen(v); if (!v) setReturnGoldsmithId(''); }}
          companyId={companyId}
          goldsmithId={returnGoldsmithId}
          goldsmithName={(goldsmiths.find((g) => g.id === returnGoldsmithId) || {}).name}
          filter={{ material: 'pearl', packetId: packet.id }}
          onSuccess={() => { setReturnFromGsOpen(false); setReturnGoldsmithId(''); load(); }}
        />
      )}

      {/* Phase 11 — Convert to Stock (full or partial, with target packet and remainder action) */}
      <ConvertToStockDialog
        open={convertOpen}
        onOpenChange={setConvertOpen}
        packetId={id}
        onConverted={() => { setConvertOpen(false); load(); }}
      />

      {/* Wave 3 — Packet Transfer */}
      <PacketTransferDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        material="pearl"
        companyId={companyId}
        defaultSourcePacketId={packet.id}
        onTransferred={() => { setTransferOpen(false); load(); }}
      />

      {/* Wave 3.47 — Re-cut (from broken) and Send-to-Broken (from normal) */}
      {recutOpen && packet?.packet_type === 'broken' && (
        <RecutFromBrokenDialog
          open={recutOpen}
          onOpenChange={setRecutOpen}
          material="pearl"
          brokenPacket={packet}
          companyId={companyId}
          onCompleted={() => { setRecutOpen(false); load(); }}
        />
      )}
      {sendToBrokenOpen && packet?.packet_type !== 'broken' && (
        <SendToBrokenDialog
          open={sendToBrokenOpen}
          onOpenChange={setSendToBrokenOpen}
          material="pearl"
          sourcePacket={packet}
          companyId={companyId}
          onCompleted={() => { setSendToBrokenOpen(false); load(); }}
        />
      )}
      {/* Phase 18 — Purchase Return */}
      <PurchaseReturnDialog
        open={purchaseReturnOpen}
        onOpenChange={setPurchaseReturnOpen}
        mode="packet"
        material="pearl"
        companyId={companyId}
        packet={packet}
        availableVendors={purchaseReturnVendors}
        onReturned={() => { setPurchaseReturnOpen(false); load(); loadPurchaseReturnVendors(); }}
      />

      {/* Phase 18 — Sale of Raw Material */}
      <SaleDialog
        open={saleOpen}
        onOpenChange={setSaleOpen}
        material="pearl"
        packet={packet}
        onSold={() => { setSaleOpen(false); load(); }}
      />

      {/* Wave 3.56 — Approval Return (on-approval packets only) */}
      <ApprovalReturnDialog
        open={approvalReturnOpen}
        onOpenChange={setApprovalReturnOpen}
        material="pearl"
        packet={packet}
        onReturned={() => { setApprovalReturnOpen(false); load(); }}
      />

      {/* Wave 3.59 — Issue for Recutting (broken packets only) */}
      <IssueForRecutDialog
        open={issueRecutOpen}
        onOpenChange={setIssueRecutOpen}
        material="pearl"
        packet={packet}
        onIssued={() => { setIssueRecutOpen(false); load(); }}
      />

      {/* Phase 10 — Fixed rate edit dialog (SA / CA only) */}
      <Dialog open={rateDlgOpen} onOpenChange={setRateDlgOpen}>
        <DialogContent className="max-w-md" data-testid="packet-rate-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Fixed Rate</DialogTitle>
            <DialogDescription>
              Update the fixed rate per carat for {packet.packet_no}. Existing ledger entries remain unchanged; the new rate applies to future receipts and the current balance valuation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fixed Rate (₹/ct)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                data-testid="packet-rate-edit-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">Current: ₹{Number(packet.fixed_rate_per_ct || packet.current_rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}/ct</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDlgOpen(false)}>Cancel</Button>
            <Button onClick={saveFixedRate} className="luxury-button" data-testid="packet-rate-edit-save">Save Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 13.4 — Request Delete (strict CA → SA approval workflow) */}
      <Dialog open={deleteDlgOpen} onOpenChange={(v) => { setDeleteDlgOpen(v); if (!v) setDeleteReason(''); }}>
        <DialogContent className="max-w-md" data-testid="packet-delete-request-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <ShieldAlert size={18} /> Request Packet Deletion
            </DialogTitle>
            <DialogDescription>
              A pearl packet can only be deleted via a strict <strong>2-stage approval</strong> workflow
              (Company Admin → Super Admin). The packet&apos;s ledger history will be preserved (soft-delete).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              <div className="flex items-center justify-between">
                <span className="font-mono">{packet.packet_no}</span>
                <span className="text-slate-500">{packet.classification?.shape_name || packet.classification_shape}</span>
              </div>
              <div className={`mt-1 grid ${canViewValues ? 'grid-cols-3' : 'grid-cols-2'} gap-2 text-[11px]`}>
                <div><span className="text-slate-500">Carats:</span> {formatCarats(packet.balance_carats)}</div>
                <div><span className="text-slate-500">Pieces:</span> {packet.balance_pieces}</div>
                {canViewValues && <div><span className="text-slate-500">Value:</span> {formatINR(packet.balance_value)}</div>}
              </div>
            </div>
            {isCompanyAdmin && (
              <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                As a <strong>Company Admin</strong>, Stage 1 will be auto-approved and the request will go
                directly to <strong>Super Admin</strong> for final approval.
              </div>
            )}
            {isSuperAdmin && (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Even as Super Admin, this delete is routed through the workflow.
                You may approve both stages from the Requests page.
              </div>
            )}
            <div>
              <Label>Reason for deletion <span className="text-rose-600">*</span></Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="e.g. Created in error, duplicate entry, packet retired…"
                rows={3}
                data-testid="packet-delete-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDlgOpen(false)} data-testid="packet-delete-cancel">Cancel</Button>
            <Button
              onClick={submitDeleteRequest}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={submittingDelete || !deleteReason.trim()}
              data-testid="packet-delete-submit"
            >
              {submittingDelete ? 'Submitting…' : 'Submit Delete Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wave 3.34 — Print Ledger PDF moved into the Actions dropdown and
          now honors the active Period filter (or downloads the entire history
          when blank). The old radio-button "all vs date range" dialog has
          been retired. */}

      {/* Wave 3.34 — Receive + Issue dialogs are rendered here (controlled
          via state) so they can be triggered from the Actions dropdown. */}
      <ReceiveDialog
        open={receiveOpen}
        onOpenChange={setReceiveOpen}
        packet={packet}
        vendors={vendors}
        reloadParties={loadParties}
        reload={load}
        companyId={companyId}
      />
      <IssueDialog
        open={issueOpen}
        onOpenChange={setIssueOpen}
        packet={packet}
        goldsmiths={goldsmiths}
        vendors={vendors}
        companyId={companyId}
        reloadParties={loadParties}
        reload={load}
      />

      {/* Wave 3.34 — Period filter card (mirrors Gold Ledger) */}
      <Card className="p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="packet-ledger-period-card">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 shrink-0">Period</div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            From
            <DatePicker
              value={period.from}
              onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
              className="h-8 text-xs w-[170px]"
              data-testid="packet-ledger-period-from"
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            To
            <DatePicker
              value={period.to}
              onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
              className="h-8 text-xs w-[170px]"
              data-testid="packet-ledger-period-to"
            />
          </label>
          {(period.from || period.to) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setPeriod({ from: '', to: '' })}
              className="h-8 px-2 text-xs"
              data-testid="packet-ledger-period-clear"
            >
              Clear
            </Button>
          )}
          <span className="text-[11px] text-slate-500 ml-1">
            {period.from || period.to
              ? `Showing ${period.from || '—'} to ${period.to || '—'}`
              : 'Showing all-time entries'}
          </span>
        </div>
      </Card>

      {/* Wave 3.34 — Opening / Receipts / Issues / Closing summary cards.
          Diamonds (and Color Stones / Pearls) show dual Carats + Pieces. */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Card className="p-4" data-testid="packet-ledger-summary-opening">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Opening{period.from ? ` (as on ${period.from})` : ''}</div>
            <div className="font-mono tabular-nums text-base text-[var(--ink-navy)] mt-1">{formatCarats(summary.opening.carats)} ct</div>
            <div className="text-xs text-slate-500">{summary.opening.pieces} pcs{canViewValues ? ` · ${formatINR(summary.opening.value)}` : ''}</div>
          </Card>
          <Card className="p-4" data-testid="packet-ledger-summary-receipts">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Period Receipts</div>
            <div className="font-mono tabular-nums text-base text-emerald-700 mt-1">+{formatCarats(summary.period_receipts.carats)} ct</div>
            <div className="text-xs text-slate-500">{summary.period_receipts.pieces} pcs{canViewValues ? ` · ${formatINR(summary.period_receipts.value)}` : ''}</div>
          </Card>
          <Card className="p-4" data-testid="packet-ledger-summary-issues">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Period Issues</div>
            <div className="font-mono tabular-nums text-base text-rose-700 mt-1">-{formatCarats(summary.period_issues.carats)} ct</div>
            <div className="text-xs text-slate-500">{summary.period_issues.pieces} pcs{canViewValues ? ` · ${formatINR(summary.period_issues.value)}` : ''}</div>
          </Card>
          <Card className="p-4 bg-[var(--diamond-bg)] border-[var(--diamond-border)]" data-testid="packet-ledger-summary-closing">
            <div className="text-[11px] uppercase tracking-wider text-[var(--diamond-icon)]">Closing{period.to ? ` (as on ${period.to})` : ''}</div>
            <div className="font-mono tabular-nums text-base text-[var(--ink-navy)] mt-1">{formatCarats(summary.closing.carats)} ct</div>
            <div className="text-xs text-slate-700">{summary.closing.pieces} pcs{canViewValues ? ` · ${formatINR(summary.closing.value)}` : ''}</div>
          </Card>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mb-2" data-testid="packet-ledger-view-controls">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">View</span>
        <PacketLedgerViewToggle mode={viewMode} onChange={setViewMode} />
      </div>

      <Card className="overflow-hidden">
        <div className="ledger-table overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th rowSpan={2} className="text-left">Date</th>
                <th rowSpan={2} className="text-left">Particulars</th>
                {/* Wave 3.41 — Sub-column "Value" hidden when !canViewValues */}
                <th colSpan={canViewValues ? 3 : 2} className="text-center ledger-group-sep" style={{ background: 'var(--diamond-bg)' }}>Receipts</th>
                <th colSpan={canViewValues ? 3 : 2} className="text-center ledger-group-sep" style={{ background: 'var(--color-stones-bg)' }}>Issued</th>
                <th colSpan={canViewValues ? 3 : 2} className="text-center ledger-group-sep" style={{ background: 'var(--gold-bg)' }}>Balance</th>
              </tr>
              <tr>
                <th className="text-right ledger-group-sep">Cts</th>
                <th className="text-right">Pcs</th>
                {canViewValues && <th className="text-right">Value</th>}
                <th className="text-right ledger-group-sep">Cts</th>
                <th className="text-right">Pcs</th>
                {canViewValues && <th className="text-right">Value</th>}
                <th className="text-right ledger-group-sep">Cts</th>
                <th className="text-right">Pcs</th>
                {canViewValues && <th className="text-right">Value</th>}
              </tr>
            </thead>
            <tbody data-testid="packet-ledger-table-body">
              {entries.length === 0 && <tr><td colSpan={canViewValues ? 11 : 8} className="text-center py-10 text-slate-500">No transactions yet.</td></tr>}
              {viewMode === 'merged' ? (
                <MergedLedgerRows groups={mergedGroups} canViewValues={canViewValues} onOpenGroup={setMergedDetail} />
              ) : entries.map((e) => (
                <tr
                  key={e.id}
                  className={(e.edit_request_id || e.edited_fields) ? EDITED_ROW_CLASS : ''}
                  data-testid={`ledger-row-${e.id}`}
                >
                  <td className="whitespace-nowrap text-xs text-slate-600">{formatDateTime(e.timestamp)}</td>
                  <td className="text-xs">
                    {e.type === 'transfer_in' ? (
                      <span data-testid={`ledger-transfer-in-${e.id}`}>
                        {e.transfer_kind === 'recut' ? (
                          <>Received from <span className="text-amber-700 font-medium">Broken</span> <span className="font-mono">{e.counter_packet_no || 'broken packet'}</span>
                            {e.counter_carats != null ? <span className="text-slate-500"> · re-cut {Number(e.counter_carats).toFixed(3)}→{Number(e.carats).toFixed(3)} ct</span> : null}
                          </>
                        ) : e.transfer_kind === 'break' ? (
                          <>Received from <span className="font-mono">{e.counter_packet_no || 'source packet'}</span> <span className="text-rose-700 text-[10px]">(break)</span>
                            {e.counter_carats != null ? <span className="text-slate-500"> · {Number(e.counter_carats).toFixed(3)}→{Number(e.carats).toFixed(3)} ct</span> : null}
                          </>
                        ) : (
                          <>Received from <span className="font-medium font-mono">{e.counter_packet_no || 'another packet'}</span>
                            {e.batch_transfer_id ? <span className="text-slate-500"> · multi-source batch</span> : null}
                          </>
                        )}
                      </span>
                    ) : e.type === 'transfer_out' ? (
                      <span data-testid={`ledger-transfer-out-${e.id}`}>
                        {e.transfer_kind === 'recut' ? (
                          <>Issued to <span className="font-mono">{e.counter_packet_no || 'destination'}</span> <span className="text-amber-700 text-[10px]">(re-cut)</span>
                            {e.counter_carats != null ? <span className="text-slate-500"> · {Number(e.carats).toFixed(3)}→{Number(e.counter_carats).toFixed(3)} ct</span> : null}
                          </>
                        ) : e.transfer_kind === 'break' ? (
                          <>Issued to <span className="text-rose-700 font-medium">Broken</span> <span className="font-mono">{e.counter_packet_no || 'broken packet'}</span>
                            {e.counter_carats != null ? <span className="text-slate-500"> · {Number(e.carats).toFixed(3)}→{Number(e.counter_carats).toFixed(3)} ct</span> : null}
                          </>
                        ) : (
                          <>Transferred to <span className="font-medium font-mono">{e.counter_packet_no || 'another packet'}</span>
                            {e.batch_transfer_id ? <span className="text-slate-500"> · multi-source batch</span> : null}
                          </>
                        )}
                      </span>
                    ) : e.type === 'receipt' ? (
                      <span>
                        {e.sub_type === 'goldsmith_return' ? (
                          <>Return from <span className="font-medium">{e.goldsmith_name || 'goldsmith'}</span>{e.order_no ? <> · <Link to={`/orders/${e.order_id}`} className="text-[var(--ink-navy)] hover:underline">{e.order_no}</Link></> : null}</>
                        ) : (
                          <>From <span className="font-medium">{e.vendor_name || 'Unknown'}</span></>
                        )}
                        {e.reference ? <span className="text-slate-500"> · {e.reference}</span> : null}
                      </span>
                    ) : e.issue_type === 'purchase_return' ? (
                      <span data-testid={`ledger-purchase-return-${e.id}`}>
                        Purchase Return to <span className="font-medium">{e.vendor_name || 'vendor'}</span>
                        {e.reference ? <span className="text-slate-500"> · {e.reference}</span> : null}
                      </span>
                    ) : e.issue_type === 'sale' ? (
                      <span data-testid={`ledger-sale-${e.id}`}>
                        Sale to <span className="font-medium">{e.sale_party_name || 'party'}</span>
                        {e.reference ? <span className="text-slate-500"> · {e.reference}</span> : null}
                      </span>
                    ) : e.issue_type === 'return_to_vendor' ? (
                      <span>
                        Return to <span className="font-medium">{e.vendor_name || 'vendor'}</span>
                        {e.reference ? <span className="text-slate-500"> · {e.reference}</span> : null}
                      </span>
                    ) : (
                      <span>
                        To <span className="font-medium">{e.goldsmith_name || 'Unknown'}</span>
                        {e.order_id && e.order_no && (
                          <Link to={`/orders/${e.order_id}`} className="ml-1 text-[var(--ink-navy)] underline" data-testid={`ledger-order-link-${e.id}`}>
                            · {e.order_no}
                          </Link>
                        )}
                        {e.reference ? <span className="text-slate-500"> · {e.reference}</span> : null}
                      </span>
                    )}
                    {(e.issue_memo_no || e.return_memo_no || e.purchase_return_memo_no || e.sale_memo_no || e.vendor_return_memo_no) && (
                      <div className="mt-0.5 inline-flex gap-1 flex-wrap items-center">
                        {e.issue_memo_no && <MemoLink id={e.goldsmith_issue_id} memoNo={e.issue_memo_no} testId={`ledger-memo-${e.id}`} />}
                        {e.return_memo_no && <ReturnMemoLink id={e.return_id} memoNo={e.return_memo_no} testId={`ledger-return-memo-${e.id}`} />}
                        {e.purchase_return_memo_no && (
                          <span className="inline-flex items-center text-[11px] font-mono bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded" data-testid={`ledger-pr-memo-${e.id}`}>
                            {e.purchase_return_memo_no}
                          </span>
                        )}
                        {e.sale_memo_no && (
                          <span className="inline-flex items-center text-[11px] font-mono bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid={`ledger-sale-memo-${e.id}`}>
                            {e.sale_memo_no}
                          </span>
                        )}
                        {e.vendor_return_memo_no && !e.purchase_return_memo_no && (
                          <span className="inline-flex items-center text-[11px] font-mono bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded" data-testid={`ledger-vr-memo-${e.id}`}>
                            {e.vendor_return_memo_no}
                          </span>
                        )}
                      </div>
                    )}
                    {e.note ? <div className="text-slate-500">{e.note}</div> : null}
                    {/* Wave 3.51 Phase B — Per-row Request Edit kebab.
                        Renders only for eligible rows within the T+1
                        IST window. */}
                    <PacketLedgerEditKebab
                      entry={e}
                      onSuccess={() => { load(); refreshEditLock(); }}
                    />
                  </td>
                  <td className="text-right font-mono tabular-nums ledger-group-sep">{isReceiptLedgerType(e.type) ? formatCarats(e.carats) : ''}</td>
                  <td className="text-right font-mono tabular-nums">{isReceiptLedgerType(e.type) ? e.pieces : ''}</td>
                  {canViewValues && <td className="text-right font-mono tabular-nums">{isReceiptLedgerType(e.type) ? formatINR(e.value) : ''}</td>}
                  <td className="text-right font-mono tabular-nums ledger-group-sep">{isIssueLedgerType(e.type) ? formatCarats(e.carats) : ''}</td>
                  <td className="text-right font-mono tabular-nums">{isIssueLedgerType(e.type) ? e.pieces : ''}</td>
                  {canViewValues && <td className="text-right font-mono tabular-nums">{isIssueLedgerType(e.type) ? formatINR(e.value) : ''}</td>}
                  <td className="text-right font-mono tabular-nums ledger-group-sep">{formatCarats(e.balance_carats)}</td>
                  <td className="text-right font-mono tabular-nums">{e.balance_pieces}</td>
                  {canViewValues && <td className="text-right font-mono tabular-nums">{formatINR(e.balance_value)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <MergedLedgerDialog group={mergedDetail} canViewValues={canViewValues} onClose={() => setMergedDetail(null)} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[var(--paper-warm)] rounded-md px-3 py-2 border border-[var(--divider)]">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono tabular-nums text-sm text-[var(--ink-navy)]">{value}</div>
    </div>
  );
}

/**
 * Wave 3.34 — PacketActionsMenu
 *
 * Consolidates every action that used to live on the packet detail header
 * (Receive, Issue, Return from Goldsmith, Transfer, Convert to Stock,
 * Purchase Return, Sale, Print Ledger, Request Delete) into a single
 * Shadcn DropdownMenu labeled "Actions".
 *
 * The menu does NOT own any of the action state; the parent component
 * passes onSomething callbacks + the state flags that determine whether
 * each item is visible / enabled.
 */
function PacketActionsMenu({
  packet,
  isOnApproval,
  canConvert,
  canRequestDelete,
  openDeleteReq,
  eligibleToRequestDelete,
  deleteBlockedReasons,
  returnCandidates,
  onReceive,
  onIssue,
  onReturnFromGoldsmith,
  onReturnPick,
  onConvertToStock,
  onTransfer,
  onPurchaseReturn,
  purchaseReturnVendors,
  onSale,
  onApprovalReturn,   // Wave 3.56 — Approval Return (on-approval only)
  onSendToBroken,
  onRecut,
  onIssueForRecut,   // Wave 3.59 — Broken → Vendor/Cutter for recutting
  onPrint,
  onExportExcel,
  onRequestDelete,
  printing,
  exportingExcel,
  periodActive,
}) {
  const isEmpty = Number(packet.balance_carats || 0) <= 0 && Number(packet.balance_pieces || 0) <= 0;
  const hasBalance = !isEmpty;
  // Wave 3.62 — see ColorStonePacketDetail.jsx for rationale.
  const noVendor = !(purchaseReturnVendors && purchaseReturnVendors.length);
  // Wave 3.45 Phase 2 — Broken packets cannot perform outgoing movements.
  const isBroken = packet.packet_type === 'broken';
  const purchaseReturnEligible = !isOnApproval && hasBalance && !isBroken;
  const saleEligible = !isOnApproval && hasBalance && !isBroken;
  const transferEligible = !isOnApproval && hasBalance && !isBroken;
  const convertEligible = isOnApproval && canConvert && !isBroken;
  const issueEligible = hasBalance && !isBroken;
  // Wave 3.47 — Recut (broken → normal) and Send-to-Broken (normal → broken)
  // eligibility flags. Mirrors Diamonds / Colour Stones detail pages.
  const recutEligible = isBroken && hasBalance;
  const sendToBrokenEligible = !isBroken && !isOnApproval && hasBalance;
  // Wave 3.56 — Approval Return: only visible for on-approval packets
  // that still have in-hand balance.
  const approvalReturnEligible = isOnApproval && hasBalance && !isBroken;
  // Wave 3.59 — Issue-for-recutting (broken → vendor/cutter).
  const issueForRecutEligible = isBroken && hasBalance;
  const returnZero = (returnCandidates || []).length === 0;
  const returnSingle = (returnCandidates || []).length === 1;
  const returnLabel = returnSingle
    ? `Return from ${returnCandidates[0].name}`
    : 'Return from Goldsmith';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="luxury-button h-9" data-testid="packet-actions-trigger">
          <MoreHorizontal size={14} className="mr-1.5" />
          Actions
          <ChevronDown size={14} className="ml-1.5 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
          Packet {packet.packet_no}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Wave 3.48 Fix #3 — Hide Receive (vendor purchase) on broken packets. */}
        {!isBroken && (
          <DropdownMenuItem onSelect={onReceive} data-testid="packet-action-receive">
            <ArrowDownToLine size={14} className="mr-2 text-emerald-700" />
            Receive
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onSelect={(e) => { if (!issueEligible) { e.preventDefault(); return; } onIssue(); }}
          disabled={!issueEligible}
          data-testid="packet-action-issue"
        >
          <ArrowUpFromLine size={14} className="mr-2 text-rose-700" />
          Issue
          {isEmpty && <span className="ml-auto text-[10px] text-slate-400">empty</span>}
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={(e) => {
            if (returnZero) { e.preventDefault(); return; }
            if (returnSingle) {
              onReturnFromGoldsmith(returnCandidates[0].id);
            } else {
              onReturnPick();
            }
          }}
          disabled={returnZero}
          data-testid="packet-action-return-from-goldsmith"
        >
          <ArrowDownToLine size={14} className="mr-2 text-amber-700" />
          {returnLabel}
          {returnZero && <span className="ml-auto text-[10px] text-slate-400">none</span>}
        </DropdownMenuItem>

        {(convertEligible || approvalReturnEligible || transferEligible || purchaseReturnEligible || saleEligible) && (
          <DropdownMenuSeparator />
        )}

        {convertEligible && (
          <DropdownMenuItem onSelect={onConvertToStock} data-testid="packet-action-convert-to-stock">
            <ShieldCheck size={14} className="mr-2 text-emerald-700" />
            Convert to Stock
          </DropdownMenuItem>
        )}

        {approvalReturnEligible && (
          <DropdownMenuItem
            onSelect={onApprovalReturn}
            data-testid="packet-action-approval-return"
          >
            <ArrowDownToLine size={14} className="mr-2 text-amber-700" />
            Approval Return
          </DropdownMenuItem>
        )}

        {transferEligible && (
          <DropdownMenuItem onSelect={onTransfer} data-testid="packet-action-transfer">
            <ArrowRightLeft size={14} className="mr-2 text-[var(--ink-navy)]" />
            Transfer
          </DropdownMenuItem>
        )}

        {purchaseReturnEligible && (
          <DropdownMenuItem
            onSelect={(e) => {
              if (noVendor) {
                e.preventDefault();
                toast.error('Original vendor for this packet could not be resolved. Purchase Return is unavailable.');
                return;
              }
              onPurchaseReturn();
            }}
            disabled={noVendor}
            data-testid="packet-action-purchase-return"
          >
            <ArrowDownToLine size={14} className="mr-2 text-amber-800" />
            Purchase Return
            {noVendor && <span className="ml-auto text-[10px] text-slate-400">no vendor</span>}
          </DropdownMenuItem>
        )}

        {saleEligible && (
          <DropdownMenuItem onSelect={onSale} data-testid="packet-action-sale">
            <ShoppingCart size={14} className="mr-2 text-emerald-800" />
            Sale
          </DropdownMenuItem>
        )}

        {/* Wave 3.47 — Send-to-Broken (normal → broken) */}
        {sendToBrokenEligible && (
          <DropdownMenuItem
            onSelect={onSendToBroken}
            data-testid="packet-action-send-to-broken"
          >
            <PackageX size={14} className="mr-2 text-rose-700" />
            Send to Broken
          </DropdownMenuItem>
        )}

        {/* Wave 3.47 — Re-cut (broken → normal) */}
        {recutEligible && (
          <DropdownMenuItem
            onSelect={onRecut}
            data-testid="packet-action-recut"
          >
            <Scissors size={14} className="mr-2 text-amber-700" />
            Re-cut to Normal Packet
          </DropdownMenuItem>
        )}

        {/* Wave 3.59 — Issue for Recutting (broken → vendor/cutter) */}
        {issueForRecutEligible && (
          <DropdownMenuItem
            onSelect={onIssueForRecut}
            data-testid="packet-action-issue-for-recut"
          >
            <Scissors size={14} className="mr-2 text-amber-700" />
            Issue for Recutting
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); if (!printing) onPrint(); }}
          data-testid="packet-action-print"
        >
          <Printer size={14} className="mr-2 text-[var(--ink-navy)]" />
          {printing ? 'Generating PDF…' : 'Export Ledger PDF'}
          {periodActive && !printing && (
            <span className="ml-auto text-[10px] text-slate-400">period</span>
          )}
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); if (!exportingExcel) onExportExcel(); }}
          data-testid="packet-action-export-excel"
        >
          <FileSpreadsheet size={14} className="mr-2 text-emerald-700" />
          {exportingExcel ? 'Generating Excel…' : 'Export Ledger Excel'}
          {periodActive && !exportingExcel && (
            <span className="ml-auto text-[10px] text-slate-400">period</span>
          )}
        </DropdownMenuItem>

        {canRequestDelete && !openDeleteReq && (
          <>
            <DropdownMenuSeparator />
            {!eligibleToRequestDelete && (deleteBlockedReasons || []).length > 0 ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <DropdownMenuItem
                        onSelect={(e) => e.preventDefault()}
                        disabled
                        className="text-rose-600"
                        data-testid="packet-action-request-delete-disabled"
                      >
                        <Trash2 size={14} className="mr-2" />
                        Request Delete
                        <span className="ml-auto text-[10px] text-slate-400">blocked</span>
                      </DropdownMenuItem>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <div className="text-xs">
                      <div className="font-medium mb-1">Cannot request delete:</div>
                      <ul className="list-disc pl-4 space-y-0.5" data-testid="packet-delete-blocked-reasons">
                        {deleteBlockedReasons.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <DropdownMenuItem
                onSelect={onRequestDelete}
                className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
                data-testid="packet-action-request-delete"
              >
                <Trash2 size={14} className="mr-2" />
                Request Delete
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Wave 3.34 — ReturnPickerDialog
 *
 * Multi-candidate picker for "Return from Goldsmith" when more than one
 * goldsmith currently holds balance from a packet. When only one candidate
 * exists the parent routes directly to the ReturnDialog and skips this step.
 */
function ReturnPickerDialog({ open, onOpenChange, packet, candidates, onPick }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Pick goldsmith holding this packet</DialogTitle>
          <DialogDescription>
            Multiple goldsmiths currently hold balance from {packet?.packet_no || 'this packet'}. Choose which one to return from.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {(candidates || []).length === 0 && (
            <div className="text-sm text-slate-500 py-4 text-center border border-dashed rounded">
              No goldsmiths currently hold balance from this packet.
            </div>
          )}
          {(candidates || []).map((c) => (
            <button
              key={c.id}
              onClick={() => onPick(c.id)}
              className="w-full flex items-center justify-between gap-2 p-3 border border-[var(--divider)] rounded-md hover:bg-slate-50 text-left"
              data-testid={`packet-return-pick-${c.id}`}
            >
              <span className="text-sm font-medium text-[var(--ink-navy)]">{c.name}</span>
              <span className="text-xs font-mono text-slate-600">{formatCarats(c.carats)} ct · {c.pieces} pcs</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ open, onOpenChange, packet, vendors, reloadParties, reload, companyId }) {
  const [form, setForm] = useState({ carats: '', pieces: '', rate_per_ct: '', vendor_id: '', reference: '', note: '', credit_days: '' });
  const [vendorDlgOpen, setVendorDlgOpen] = useState(false);
  // Wave 3.48 Fix #1 — guard against double-click submission.
  const [submitting, setSubmitting] = useState(false);

  // Phase 10 — Lock rate input when packet is Fixed Rate AND already has a fixed rate set
  const isFixed = packet.valuation_mode === 'fixed';
  const fixedLocked = isFixed && Number(packet.fixed_rate_per_ct || 0) > 0;
  // Wave 3.36 — On-approval receipts don't create a payable, so the
  // credit-days input is hidden until the packet becomes purchased.
  const showCreditDays = packet.purchase_status !== 'on_approval';

  // Wave 3.36 — Pre-fill credit days from the selected vendor's default.
  useEffect(() => {
    if (!form.vendor_id) return;
    if (form.credit_days !== '') return; // user already typed; respect it
    const v = vendors.find((vv) => vv.id === form.vendor_id);
    if (v && v.default_credit_days != null && v.default_credit_days > 0) {
      setForm((f) => ({ ...f, credit_days: String(v.default_credit_days) }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.vendor_id]);

  const submit = async () => {
    if (submitting) return;           // Wave 3.48 Fix #1 — debounce
    setSubmitting(true);
    try {
      const ct = Number(form.carats);
      const pcs = Number(form.pieces);
      if (!form.vendor_id) { toast.error('Please select a vendor (required for every purchase)'); return; }
      // Wave 3.49 Rule 1 — receipts require BOTH carats AND pieces > 0.
      const ruleErr = validateMovementQty(ct, pcs, { context: 'vendor receipt', packetNo: packet.packet_no });
      if (ruleErr) { toast.error(ruleErr); return; }
      if (packet.valuation_mode === 'running_avg' && !Number(form.rate_per_ct)) { toast.error('Rate per carat required for Running Average packets'); return; }
      if (!fixedLocked && packet.valuation_mode === 'fixed' && !Number(form.rate_per_ct)) { toast.error('Rate per carat required for first receipt on a Fixed Rate packet'); return; }
      const vendor = vendors.find(v => v.id === form.vendor_id);
      const payload = {
        type: 'receipt', carats: ct, pieces: pcs, reference: form.reference, note: form.note,
        vendor_id: form.vendor_id, vendor_name: vendor?.name,
      };
      // Only send rate when not locked; backend will ignore on fixed-locked packets anyway.
      if (!fixedLocked && (packet.valuation_mode === 'running_avg' || form.rate_per_ct)) {
        payload.rate_per_ct = Number(form.rate_per_ct);
      }
      // Wave 3.36 — Optional credit days (only meaningful for purchased packets).
      if (showCreditDays && form.credit_days !== '') {
        const n = parseInt(form.credit_days, 10);
        if (!Number.isNaN(n) && n > 0) payload.credit_days = n;
      }
      await api.post(`/pearls/packets/${packet.id}/receipt`, payload);
      toast.success('Receipt recorded');
      onOpenChange(false);
      setForm({ carats: '', pieces: '', rate_per_ct: '', vendor_id: '', reference: '', note: '', credit_days: '' });
      reload();
    } catch (e) { toast.error(extractError(e, 'Receipt failed')); }
    finally { setSubmitting(false); }
  };

  const preview = () => {
    const ct = Number(form.carats) || 0;
    if (!ct) return null;
    if (packet.valuation_mode === 'running_avg') {
      const rate = Number(form.rate_per_ct) || 0;
      const newCt = packet.balance_carats + ct;
      const newVal = packet.balance_value + ct * rate;
      const newRate = newCt > 0 ? newVal / newCt : 0;
      return { newCt, newVal, newRate };
    } else {
      const rate = fixedLocked ? Number(packet.fixed_rate_per_ct) : (Number(form.rate_per_ct) || packet.current_rate_per_ct);
      const newCt = packet.balance_carats + ct;
      const newVal = packet.balance_value + ct * rate;
      return { newCt, newVal, newRate: rate };
    }
  };
  const p = preview();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Receive into {packet.packet_no}</DialogTitle>
          <DialogDescription>Add new stock to this packet. Vendor selection is mandatory.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Vendor <span className="text-rose-600">*</span></Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <SearchableSelect
                  items={vendors}
                  value={form.vendor_id}
                  onValueChange={(v) => setForm({ ...form, vendor_id: v || '' })}
                  itemToLabel={(v) => v.name}
                  itemToSubLabel={(v) => [v.phone, v.email].filter(Boolean).join(' · ') || null}
                  searchKeys={['name', 'phone', 'email']}
                  placeholder={vendors.length === 0 ? 'No vendors — create one →' : 'Select vendor…'}
                  searchPlaceholder="Search vendor…"
                  disabled={vendors.length === 0}
                  testId="receive-vendor-select"
                />
              </div>
              <Button type="button" variant="outline" size="icon" onClick={() => setVendorDlgOpen(true)} data-testid="receive-create-vendor-button" title="Create new vendor">
                <Plus size={14} />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Carats</Label><Input type="number" step="0.01" value={form.carats} onChange={(e) => setForm({ ...form, carats: e.target.value })} data-testid="receive-carats-input" /></div>
            <div><Label>Pieces</Label><Input type="number" step="1" value={form.pieces} onChange={(e) => setForm({ ...form, pieces: e.target.value })} data-testid="receive-pieces-input" /></div>
          </div>
          <div>
            {fixedLocked ? (
              <>
                <Label>Rate ₹/ct (locked)</Label>
                <Input
                  type="number" step="0.01"
                  value={Number(packet.fixed_rate_per_ct || 0)}
                  disabled
                  readOnly
                  className="bg-slate-100 cursor-not-allowed"
                  data-testid="receive-rate-input-locked"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Fixed Rate is locked at ₹{Number(packet.fixed_rate_per_ct || 0).toLocaleString('en-IN')}/ct. To change it, edit the rate from the packet header (Super Admin or Company Admin only).
                </div>
              </>
            ) : (
              <>
                <Label>{packet.valuation_mode === 'running_avg' ? 'Rate ₹/ct (required)' : `Rate ₹/ct (required — sets the Fixed Rate)`}</Label>
                <Input type="number" step="0.01" value={form.rate_per_ct} onChange={(e) => setForm({ ...form, rate_per_ct: e.target.value })} data-testid="receive-rate-input" />
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="receive-reference-input" /></div>
            <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="receive-note-input" /></div>
          </div>
          {/* Wave 3.36 — Credit days capture (purchased packets only). */}
          {showCreditDays && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Credit Days <span className="text-slate-400 text-[10px]">(optional)</span></Label>
                <Input
                  type="number" min={0} max={365}
                  placeholder="e.g. 30 — leave blank for none"
                  value={form.credit_days}
                  onChange={(e) => setForm({ ...form, credit_days: e.target.value.replace(/[^0-9]/g, '') })}
                  data-testid="receive-credit-days-input"
                />
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Sets due date for vendor payment & reminders (T-7, T-1, T0).
                </div>
              </div>
              <div className="self-end text-[11px] text-slate-500">
                {form.credit_days && Number(form.credit_days) > 0 ? (
                  <span data-testid="receive-due-date-preview">
                    Due date will be approximately <strong>
                      {(() => {
                        const d = new Date();
                        d.setDate(d.getDate() + Number(form.credit_days));
                        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                      })()}
                    </strong>.
                  </span>
                ) : (
                  <span className="text-slate-400">No due date / no reminders.</span>
                )}
              </div>
            </div>
          )}
          {p && (
            <Card className="p-3 bg-[var(--paper-warm)]">
              <div className="text-xs text-slate-500">After this receipt</div>
              <div className="text-sm font-mono text-[var(--ink-navy)]">
                Balance: {formatCarats(p.newCt)} ct · {formatINR(p.newVal)}
                {packet.valuation_mode === 'running_avg' && <> · New avg rate: {formatINR(p.newRate)}/ct</>}
              </div>
            </Card>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="luxury-button" data-testid="receive-submit-button" disabled={submitting || !form.vendor_id}>
            {submitting ? 'Posting…' : 'Confirm Receipt'}
          </Button>
        </DialogFooter>
      </DialogContent>
      <VendorFormDialog
        open={vendorDlgOpen}
        onOpenChange={setVendorDlgOpen}
        companyId={companyId}
        onCreated={async (v) => { await reloadParties(); setForm((f) => ({ ...f, vendor_id: v.id })); }}
      />
    </Dialog>
  );
}

function IssueDialog({ open, onOpenChange, packet, goldsmiths, vendors, companyId, reloadParties, reload }) {
  const isOnApproval = packet.purchase_status === 'on_approval';
  const [issueMode, setIssueMode] = useState('goldsmith'); // 'goldsmith' | 'return_to_vendor'
  const [form, setForm] = useState({
    carats: '', pieces: '', goldsmith_id: '', order_id: '',
    requirement_id: '', mark_complete: false, reference: '', note: '',
    note_auto: false, // tracks whether the note was auto-filled from a requirement
    vendor_id: '', // for return_to_vendor
    return_rate_per_ct: '', // Phase 10: optional custom return rate (defaults to packet's purchase rate)
  });
  const [orders, setOrders] = useState([]);
  const [orderRequirements, setOrderRequirements] = useState([]);
  const [newGs, setNewGs] = useState({ open: false, name: '', phone: '' });
  const [newOrder, setNewOrder] = useState({ open: false, title: '', description: '' });
  const [newReq, setNewReq] = useState({ open: false, classification_label: '', description: '', required_carats: '', required_pieces: '' });

  // Pre-select vendor from packet's first receipt
  useEffect(() => {
    if (isOnApproval && open && !form.vendor_id) {
      api.get(`/pearls/packets/${packet.id}/ledger`).then(({ data }) => {
        const first = (data.entries || []).find(e => e.type === 'receipt');
        if (first?.vendor_id) setForm((f) => ({ ...f, vendor_id: first.vendor_id }));
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [open, isOnApproval, packet.id]);

  const loadOrders = async (gid) => {
    if (!gid) { setOrders([]); return; }
    const { data } = await api.get(`/orders/by-goldsmith/${gid}`, { params: { status: 'open' } });
    setOrders(data);
  };
  const loadOrderDetail = async (oid) => {
    if (!oid) { setOrderRequirements([]); return; }
    const { data } = await api.get(`/orders/${oid}`);
    // Show open + partial requirements that are for pearls
    const reqs = (data.requirements || []).filter(r =>
      r.material_type === 'pearl' && r.status !== 'complete'
    );
    setOrderRequirements(reqs);
  };
  useEffect(() => { loadOrders(form.goldsmith_id); setForm((f) => ({ ...f, order_id: '', requirement_id: '' })); /* eslint-disable-next-line */ }, [form.goldsmith_id]);
  useEffect(() => { loadOrderDetail(form.order_id); setForm((f) => ({ ...f, requirement_id: '' })); /* eslint-disable-next-line */ }, [form.order_id]);

  const createGoldsmithInline = async () => {
    if (!newGs.name) { toast.error('Goldsmith name required'); return; }
    try {
      const { data } = await api.post('/masters/goldsmiths', { name: newGs.name, phone: newGs.phone, company_id: companyId });
      toast.success('Goldsmith created'); await reloadParties();
      setForm((f) => ({ ...f, goldsmith_id: data.id }));
      setNewGs({ open: false, name: '', phone: '' });
    } catch (e) { toast.error(extractError(e, 'Goldsmith create failed')); }
  };

  const createOrderInline = async () => {
    if (!form.goldsmith_id) { toast.error('Pick goldsmith first'); return; }
    if (!newOrder.title) { toast.error('Order title required'); return; }
    try {
      const { data } = await api.post('/orders/', {
        goldsmith_id: form.goldsmith_id, title: newOrder.title,
        description: newOrder.description, company_id: companyId
      });
      toast.success(`Order ${data.order_no} created`);
      setNewOrder({ open: false, title: '', description: '' });
      await loadOrders(form.goldsmith_id);
      setForm((f) => ({ ...f, order_id: data.id }));
    } catch (e) { toast.error(extractError(e, 'Order create failed')); }
  };

  const createRequirementInline = async () => {
    if (!form.order_id) { toast.error('Pick order first'); return; }
    if (!newReq.required_carats && !newReq.required_pieces && !newReq.description) { toast.error('Fill at least one requirement field'); return; }
    try {
      const payload = {
        material_type: 'pearl',
        classification_id: packet.classification_id,
        classification_label: packet.classification_schema ? `${packet.classification_schema} — ${packet.classification_shape}` : '',
        description: newReq.description,
        required_carats: Number(newReq.required_carats) || 0,
        required_pieces: Number(newReq.required_pieces) || 0,
      };
      const { data } = await api.post(`/orders/${form.order_id}/requirements`, payload);
      const added = data.requirements[data.requirements.length - 1];
      toast.success('Requirement added');
      setNewReq({ open: false, classification_label: '', description: '', required_carats: '', required_pieces: '' });
      await loadOrderDetail(form.order_id);
      setForm((f) => ({ ...f, requirement_id: added.id }));
    } catch (e) { toast.error(extractError(e, 'Requirement create failed')); }
  };

  const submit = async () => {
    try {
      const ct = Number(form.carats);
      const pcs = Number(form.pieces);
      // Wave 3.49 Rule 1 + Rule 2.
      const ruleErr = validateCaratMovement({
        txnCarats: ct, txnPieces: pcs,
        availableCarats: packet.balance_carats,
        availablePieces: packet.balance_pieces,
        packetNo: packet.packet_no,
        context: issueMode === 'return_to_vendor' ? 'return-to-vendor' : 'issue to goldsmith',
      });
      if (ruleErr) { toast.error(ruleErr); return; }
      let payload;
      if (issueMode === 'return_to_vendor') {
        if (!form.vendor_id) { toast.error('Select vendor for return'); return; }
        const vendor = (vendors || []).find(v => v.id === form.vendor_id);
        payload = {
          type: 'issue', issue_type: 'return_to_vendor',
          carats: ct, pieces: pcs,
          vendor_id: form.vendor_id, vendor_name: vendor?.name || '',
          reference: form.reference, note: form.note,
        };
        // Phase 10 — Custom return rate (fallback to purchase rate on backend if not given)
        const r = Number(form.return_rate_per_ct);
        if (form.return_rate_per_ct && r > 0) payload.rate_per_ct = r;
      } else {
        if (!form.goldsmith_id) { toast.error('Select goldsmith'); return; }
        if (!String(form.note || '').trim()) { toast.error('Note is required to issue this material'); return; }
        const goldsmith = goldsmiths.find(g => g.id === form.goldsmith_id);
        const order = orders.find(o => o.id === form.order_id);
        payload = {
          type: 'issue', issue_type: 'goldsmith',
          carats: ct, pieces: pcs,
          goldsmith_id: form.goldsmith_id, goldsmith_name: goldsmith?.name,
          order_id: form.order_id || null, order_no: order?.order_no || null,
          requirement_id: form.requirement_id || null,
          mark_requirement_complete: !!form.mark_complete,
          reference: form.reference, note: form.note,
        };
      }
      await api.post(`/pearls/packets/${packet.id}/issue`, payload);
      toast.success(issueMode === 'return_to_vendor' ? 'Returned to vendor' : 'Issued');
      onOpenChange(false);
      setForm({ carats: '', pieces: '', goldsmith_id: '', order_id: '', requirement_id: '', mark_complete: false, reference: '', note: '', note_auto: false, vendor_id: '', return_rate_per_ct: '' });
      reload();
    } catch (e) { toast.error(extractError(e, 'Issue failed')); }
  };

  const ct = Number(form.carats) || 0;
  const issueValue = ct * (packet.current_rate_per_ct || 0);
  const overBalance = ct > packet.balance_carats + 1e-9 || Number(form.pieces) > packet.balance_pieces;
  // Phase 18 — Empty packets cannot be issued (nothing to issue or return)
  const isEmpty = Number(packet.balance_carats || 0) <= 0 && Number(packet.balance_pieces || 0) <= 0;
  // Wave — Note is mandatory when issuing raw material to a goldsmith.
  const noteRequired = issueMode === 'goldsmith';
  const noteMissing = noteRequired && !String(form.note || '').trim();
  const noteHint = 'Please enter shape, sieves or average size';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (isEmpty && v) return; onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Issue from {packet.packet_no}</DialogTitle>
          <DialogDescription>Send stock to a goldsmith{isOnApproval ? ' or return to the vendor (only allowed for on-approval packets)' : ', optionally against an order’s requirement.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-slate-500">Balance: <span className="font-mono">{formatCarats(packet.balance_carats)} ct · {packet.balance_pieces} pcs</span></div>

          {isOnApproval && (
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setIssueMode('goldsmith')}
                className={`px-3 py-2 rounded-md border text-sm ${issueMode === 'goldsmith' ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white' : 'border-[var(--divider)] hover:bg-slate-50'}`}
                data-testid="issue-mode-goldsmith">Issue to Goldsmith</button>
              <button type="button" onClick={() => setIssueMode('return_to_vendor')}
                className={`px-3 py-2 rounded-md border text-sm ${issueMode === 'return_to_vendor' ? 'border-amber-500 bg-amber-500 text-white' : 'border-[var(--divider)] hover:bg-slate-50'}`}
                data-testid="issue-mode-return">Return to Vendor</button>
            </div>
          )}

          {issueMode === 'return_to_vendor' && isOnApproval && (
            <div className="space-y-3">
              <div>
                <Label>Vendor <span className="text-rose-600">*</span></Label>
                {/* Vendor must be the one this packet was purchased from — auto-resolved from the first receipt entry and locked. */}
                {(() => {
                  const purchaseVendor = (vendors || []).find((v) => v.id === form.vendor_id);
                  return (
                    <Input
                      value={purchaseVendor?.name || (form.vendor_id ? 'Loading…' : 'Resolving purchase vendor…')}
                      readOnly
                      disabled
                      className="bg-slate-100 text-slate-700 cursor-not-allowed"
                      data-testid="issue-return-vendor-locked"
                    />
                  );
                })()}
                <div className="text-[10px] text-slate-500 mt-1">
                  On-approval packets must be returned to the vendor they were purchased from. The vendor is auto-selected from this packet's purchase record.
                </div>
              </div>
              <div>
                <Label>Return Rate ₹/ct <span className="text-slate-400">(optional)</span></Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.return_rate_per_ct}
                  onChange={(e) => setForm({ ...form, return_rate_per_ct: e.target.value })}
                  placeholder={`Defaults to purchase rate ₹${Number(packet.current_rate_per_ct || 0).toLocaleString('en-IN')}/ct`}
                  data-testid="issue-return-rate-input"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  Leave empty to credit at the packet's purchase rate. Enter a value to override (e.g. price-renegotiated returns).
                </div>
              </div>
            </div>
          )}

          {issueMode === 'goldsmith' && (
          <>
          <div>
            <Label>Goldsmith <span className="text-rose-600">*</span></Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <SearchableSelect
                  items={goldsmiths}
                  value={form.goldsmith_id}
                  onValueChange={(v) => setForm({ ...form, goldsmith_id: v || '' })}
                  itemToLabel={(g) => g.name}
                  itemToSubLabel={(g) => [g.phone, g.email].filter(Boolean).join(' · ') || null}
                  searchKeys={['name', 'phone', 'email']}
                  placeholder={goldsmiths.length === 0 ? 'No goldsmiths — create one' : 'Select goldsmith'}
                  searchPlaceholder="Search goldsmith…"
                  disabled={goldsmiths.length === 0}
                  testId="issue-goldsmith-select"
                />
              </div>
              <Button type="button" variant="outline" size="icon" onClick={() => setNewGs({ ...newGs, open: !newGs.open })} data-testid="issue-create-goldsmith-button" title="Create new goldsmith"><Plus size={14} /></Button>
            </div>
            {newGs.open && (
              <Card className="p-3 mt-2 bg-[var(--paper-warm)]">
                <div className="text-xs font-medium mb-2">Quick-create goldsmith</div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Goldsmith name" value={newGs.name} onChange={(e) => setNewGs({ ...newGs, name: e.target.value })} data-testid="issue-new-goldsmith-name" />
                  <Input placeholder="Phone (optional)" value={newGs.phone} onChange={(e) => setNewGs({ ...newGs, phone: e.target.value })} />
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <Button variant="ghost" size="sm" onClick={() => setNewGs({ open: false, name: '', phone: '' })}>Cancel</Button>
                  <Button size="sm" onClick={createGoldsmithInline} className="luxury-button" data-testid="issue-new-goldsmith-submit">Create &amp; Use</Button>
                </div>
              </Card>
            )}
          </div>
          {form.goldsmith_id && (
            <div>
              <Label>Order Form</Label>
              <div className="flex gap-2">
              <div className="flex-1">
                {(() => {
                  const orderItems = [
                    { id: 'none', order_no: 'None', title: '(no order)', stage: '' },
                    ...orders,
                  ];
                  return (
                    <SearchableSelect
                      items={orderItems}
                      value={form.order_id || 'none'}
                      onValueChange={(v) => setForm({ ...form, order_id: !v || v === 'none' ? '' : v })}
                      itemToLabel={(o) => (o.id === 'none' ? 'None (no order)' : `${o.order_no} — ${o.title} (${o.stage})`)}
                      searchKeys={['order_no', 'title', 'stage']}
                      placeholder="None"
                      searchPlaceholder="Search order…"
                      testId="issue-order-select"
                    />
                  );
                })()}
              </div>
              <Button type="button" variant="outline" size="icon" onClick={() => setNewOrder({ ...newOrder, open: !newOrder.open })} data-testid="issue-create-order-button" title="Create new order"><Plus size={14} /></Button>
            </div>
              {newOrder.open && (
                <Card className="p-3 mt-2 bg-[var(--paper-warm)]">
                  <div className="text-xs font-medium mb-2">Quick-create order</div>
                  <div className="space-y-2">
                    <Input placeholder="Order title (e.g. Necklace - Customer X)" value={newOrder.title} onChange={(e) => setNewOrder({ ...newOrder, title: e.target.value })} data-testid="issue-new-order-title" />
                    <Textarea placeholder="Description" value={newOrder.description} onChange={(e) => setNewOrder({ ...newOrder, description: e.target.value })} />
                  </div>
                  <div className="flex justify-end gap-2 mt-2">
                    <Button variant="ghost" size="sm" onClick={() => setNewOrder({ open: false, title: '', description: '' })}>Cancel</Button>
                    <Button size="sm" onClick={createOrderInline} className="luxury-button" data-testid="issue-new-order-submit">Create &amp; Use</Button>
                  </div>
                </Card>
              )}
            </div>
          )}
          {form.order_id && (
            <div>
              <Label>Requirement (optional)</Label>
              <div className="flex gap-2">
              <div className="flex-1">
                {(() => {
                  const reqItems = orderRequirements.map((r) => ({
                    // Wave 3.68 — Shape - Size - Sieves · pending X ct / Y pcs
                    id: r.id,
                    label: formatRequirementDropdownLabel(r),
                  }));
                  const items = [{ id: 'none', label: 'None' }, ...reqItems];
                  return (
                    <SearchableSelect
                      items={items}
                      value={form.requirement_id || 'none'}
                      onValueChange={(v) => {
                        const rid = !v || v === 'none' ? '' : v;
                        setForm((f) => {
                          const patch = { ...f, requirement_id: rid };
                          const rq = orderRequirements.find((r) => r.id === rid);
                          const prefill = rq ? String(rq.note || rq.sieves || '').trim() : '';
                          if ((f.note_auto || !String(f.note || '').trim()) && prefill) {
                            patch.note = prefill;
                            patch.note_auto = true;
                          }
                          return patch;
                        });
                      }}
                      itemToValue={(it) => it.id}
                      itemToLabel={(it) => it.label}
                      searchKeys={['label']}
                      placeholder="None"
                      searchPlaceholder="Search requirement…"
                      testId="issue-requirement-select"
                    />
                  );
                })()}
              </div>
              <Button type="button" variant="outline" size="icon" onClick={() => setNewReq({ ...newReq, open: !newReq.open })} data-testid="issue-create-req-button" title="Add requirement"><Plus size={14} /></Button>
            </div>
              {newReq.open && (
                <Card className="p-3 mt-2 bg-[var(--paper-warm)]">
                  <div className="text-xs font-medium mb-2">Add pearl requirement to this order</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Required carats" type="number" step="0.01" value={newReq.required_carats} onChange={(e) => setNewReq({ ...newReq, required_carats: e.target.value })} />
                    <Input placeholder="Required pieces" type="number" step="1" value={newReq.required_pieces} onChange={(e) => setNewReq({ ...newReq, required_pieces: e.target.value })} />
                  </div>
                  <Textarea className="mt-2" placeholder="Description (optional)" value={newReq.description} onChange={(e) => setNewReq({ ...newReq, description: e.target.value })} />
                  <div className="flex justify-end gap-2 mt-2">
                    <Button variant="ghost" size="sm" onClick={() => setNewReq({ open: false, classification_label: '', description: '', required_carats: '', required_pieces: '' })}>Cancel</Button>
                    <Button size="sm" onClick={createRequirementInline} className="luxury-button" data-testid="issue-new-req-submit">Add &amp; Use</Button>
                  </div>
                </Card>
              )}
              {form.requirement_id && (
                <label className="flex items-center gap-2 mt-2 text-xs text-slate-700">
                  <Checkbox checked={form.mark_complete} onCheckedChange={(v) => setForm({ ...form, mark_complete: !!v })} data-testid="issue-mark-complete-checkbox" />
                  <span><CheckCircle2 size={12} className="inline mr-1 text-emerald-600" />Mark requirement as fully met after this issue (even if quantities don&apos;t match)</span>
                </label>
              )}
            </div>
          )}
          </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Carats</Label><Input type="number" step="0.01" value={form.carats} onChange={(e) => setForm({ ...form, carats: e.target.value })} data-testid="issue-carats-input" /></div>
            <div><Label>Pieces</Label><Input type="number" step="1" value={form.pieces} onChange={(e) => setForm({ ...form, pieces: e.target.value })} data-testid="issue-pieces-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
            <div>
              <Label>Note {noteRequired && <span className="text-rose-600">*</span>}</Label>
              <Input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value, note_auto: false })}
                aria-invalid={noteMissing}
                className={noteMissing ? 'border-rose-300 focus-visible:ring-rose-200' : undefined}
                data-testid="issue-note-input"
              />
              {noteRequired && (
                <p className="text-[10px] text-slate-500 mt-1" data-testid="issue-note-hint">{noteHint}</p>
              )}
              {noteMissing && (
                <p className="text-[10px] text-rose-600 mt-1" data-testid="issue-note-error">Note is required to issue this material.</p>
              )}
            </div>
          </div>
          {overBalance && (
            <Card className="p-3 bg-rose-50 border-rose-200">
              <div className="text-xs text-rose-700 flex items-center gap-1"><AlertTriangle size={12} /> Exceeds available balance</div>
            </Card>
          )}
          {ct > 0 && !overBalance && (
            <Card className="p-3 bg-[var(--paper-warm)]">
              <div className="text-xs text-slate-500">Issue valuation</div>
              <div className="text-sm font-mono text-[var(--ink-navy)]">
                {formatCarats(ct)} ct × {formatINR(packet.current_rate_per_ct)}/ct = {formatINR(issueValue)}
              </div>
            </Card>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            className="luxury-button"
            disabled={overBalance || (issueMode === 'goldsmith' && !form.goldsmith_id) || (issueMode === 'return_to_vendor' && !form.vendor_id) || noteMissing}
            data-testid="issue-submit-button"
          >
            {issueMode === 'return_to_vendor' ? 'Return to Vendor' : 'Confirm Issue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
